'use client';

import {Icon, IconName} from '@components/@icons';
import {MeinText} from '@components/@typography/MeinText';
import {BusEventEnum, EventBus, IEvent} from '@core/eventBus';
import {Snackbar} from '@mui/material';
import classNames from 'classnames';
import {FC, ReactNode, SyntheticEvent, useEffect, useState} from 'react';
import styles from './MeinSnackbar.module.css';

const ICON_NAME_BY_TYPE: Record<
    NonNullable<ISnackbarMessage['type']>,
    IconName
> = {
    danger: 'alert-triangle',
    info: 'information-circle',
    success: 'check-circle',
    warning: 'alert-triangle',
};

export interface ISnackbarMessage {
    message?: ReactNode;
    action?: {label: ReactNode; onClick: () => void};
    type?: 'success' | 'danger' | 'info' | 'warning';
    duration?: number;
    key?: string;
}

export const MeinSnackbar: FC = () => {
    const [snackPack, setSnackPack] = useState<readonly ISnackbarMessage[]>([]);
    const [messageInfo, setMessageInfo] = useState<
        ISnackbarMessage | undefined
    >(undefined);
    const [isOpen, setIsOpen] = useState(false);

    const type = messageInfo?.type ?? 'info';
    const duration = messageInfo?.duration || 3000;

    const handleNewMessage = (event: IEvent<ISnackbarMessage>) => {
        setSnackPack(prev => [...prev, event.data!]);
    };

    const handleClose = (_: SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setIsOpen(false);
    };

    const handleExited = () => {
        setMessageInfo(undefined);
    };

    useEffect(() => {
        EventBus.on(BusEventEnum.SNACKBAR_MESSAGE, handleNewMessage);
    }, []);

    useEffect(() => {
        if (snackPack.length && !messageInfo) {
            // Set a new snack when we don't have an active one
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setMessageInfo({...snackPack[0]});
            setSnackPack(prev => prev.slice(1));
            setIsOpen(true);
        } else if (snackPack.length && messageInfo && isOpen) {
            // Close an active snack when a new one is added
            setIsOpen(false);
        }
    }, [snackPack, messageInfo, isOpen]);

    const iconName = ICON_NAME_BY_TYPE[type];

    return (
        <Snackbar
            className={classNames(styles.snackbar)}
            open={isOpen}
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            autoHideDuration={duration}
            onClose={handleClose}
            slotProps={{
                content: {
                    className: styles[type],
                },
                transition: {
                    onExited: handleExited,
                },
            }}
            key={messageInfo?.key}
            message={
                <div>
                    <div className={styles.messageContent}>
                        <Icon name={iconName} className={styles.icon} />
                        <MeinText className={styles.p}>
                            {messageInfo?.message}
                        </MeinText>
                        {messageInfo?.action && (
                            <button
                                className={styles.action}
                                onClick={messageInfo?.action.onClick}
                            >
                                {messageInfo?.action.label}
                            </button>
                        )}
                    </div>
                    <Icon
                        name="close"
                        className={classNames(styles.icon, styles.iconClose)}
                        onClick={handleClose}
                    />
                </div>
            }
        />
    );
};
